import { useCallback, useEffect, useMemo, useRef, useState, useContext } from "react";
import moment from "moment/moment";
import * as React from "react";
import { styled, css } from "styled-components";
import PDFPage, { PREVIEW_PADDING } from "report/report-creation/PDFPage";
import GlobalOverride from "views/logic/search/GlobalOverride";
import ReportPreviewContext from "report/report-creation/ReportPreviewContext";
import WidgetResultContext from "report/report-creation/WidgetResultContext";
import RenderedReport from "report/common/RenderedReport";
import { REPORT_PREVIEW_ID } from "report/report-creation/ReportPreview";
import useWidgetResult from "report/hooks/useWidgetResult";
import PDFExportThemeProvider from "common/providers/PDFExportThemeProvider";
export const Canvas = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  margin: 0;
  flex-grow: 1;
  flex-basis: 0;
  overflow-y: scroll;
`;
const PreviewCanvas = styled(Canvas)(
  ({ theme }) => css`
    background-color: ${theme.colors.gray[90]};
  `
);
const useObserveWidgetVisibility = () => {
  const [isVisible, setIsVisible] = useState(false);
  const widgetRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      setIsVisible(entry.isIntersecting);
    });
    if (widgetRef.current) {
      observer.observe(widgetRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, []);
  return { widgetRef, isVisible };
};
const WidgetResultProvider = ({ widgetId, dashboardId, children = void 0 }) => {
  const { widgetRef, isVisible } = useObserveWidgetVisibility();
  const reportPreviewContext = useContext(ReportPreviewContext);
  const { data } = useWidgetResult({
    dashboardId,
    widgetId,
    enabled: isVisible,
    parameterValues: reportPreviewContext.parameterValues,
    globalOverride: reportPreviewContext.globalOverride,
    staleTime: 1e3 * 60 * 60
  });
  return /* @__PURE__ */ React.createElement(WidgetResultContext.Provider, { value: data }, /* @__PURE__ */ React.createElement("div", { ref: widgetRef }, children));
};
const PDFPreview = ({ report }) => {
  var _a, _b;
  const scrollContainer = useRef(null);
  const now = useMemo(() => moment(), []);
  const contextValue = useMemo(
    () => ({
      parameterValues: report.parameterValues,
      globalOverride: GlobalOverride.builder().now(now).build()
    }),
    [now, report.parameterValues]
  );
  const handleTocClick = useCallback(
    (scale) => (event) => {
      const tocEntry = event.target;
      if (tocEntry.hash) {
        event.preventDefault();
        const widgetHeaderId = tocEntry.hash.substring(1);
        const widgetHeader = document.getElementById(widgetHeaderId);
        if (widgetHeader) {
          const widgetTopOffset = widgetHeader.offsetTop;
          scrollContainer.current.scrollTo({
            top: widgetTopOffset * scale + PREVIEW_PADDING
          });
        }
      }
    },
    []
  );
  return /* @__PURE__ */ React.createElement(ReportPreviewContext.Provider, { value: contextValue }, /* @__PURE__ */ React.createElement(PDFExportThemeProvider, { skipGlobalStyles: true }, /* @__PURE__ */ React.createElement(PreviewCanvas, { id: REPORT_PREVIEW_ID, "data-testid": "report-preview", ref: scrollContainer }, /* @__PURE__ */ React.createElement(PDFPage, { report, pageSize: (_a = report.layout) == null ? void 0 : _a.pageSize, pageOrientation: (_b = report.layout) == null ? void 0 : _b.orientation }, ({ contentWidth, pageScaleFactor }) => /* @__PURE__ */ React.createElement(
    RenderedReport,
    {
      report,
      handleTocClick: handleTocClick(pageScaleFactor),
      width: contentWidth,
      preview: true,
      widgetResultProvider: WidgetResultProvider
    }
  )))));
};
export default PDFPreview;
